import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Alert,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import AsyncStorage from '@react-native-async-storage/async-storage';

const PRESETS_KEY = '@chessmaps:clockPresets';

// Default presets
const DEFAULT_PRESETS = [
  { id: 'blitz3', name: '3 Minutes', player1Time: 180, player2Time: 180, increment: 0 },
  { id: 'blitz5', name: '5 Minutes', player1Time: 300, player2Time: 300, increment: 0 },
  { id: 'rapid10', name: '10 Minutes', player1Time: 600, player2Time: 600, increment: 0 },
];

const ChessClockSettings = ({ visible, onClose, onApply, currentSettings }) => {
  // Time inputs (in seconds)
  const [p1Hours, setP1Hours] = useState('0');
  const [p1Minutes, setP1Minutes] = useState('3');
  const [p1Seconds, setP1Seconds] = useState('0');

  const [p2Hours, setP2Hours] = useState('0');
  const [p2Minutes, setP2Minutes] = useState('3');
  const [p2Seconds, setP2Seconds] = useState('0');

  const [incrementSeconds, setIncrementSeconds] = useState('0');

  // Presets
  const [presets, setPresets] = useState(DEFAULT_PRESETS);
  const [presetName, setPresetName] = useState('');

  useEffect(() => {
    loadPresets();
  }, []);

  useEffect(() => {
    if (currentSettings) {
      const p1Total = currentSettings.player1Time;
      setP1Hours(Math.floor(p1Total / 3600).toString());
      setP1Minutes(Math.floor((p1Total % 3600) / 60).toString());
      setP1Seconds((p1Total % 60).toString());

      const p2Total = currentSettings.player2Time;
      setP2Hours(Math.floor(p2Total / 3600).toString());
      setP2Minutes(Math.floor((p2Total % 3600) / 60).toString());
      setP2Seconds((p2Total % 60).toString());

      setIncrementSeconds(currentSettings.increment.toString());
    }
  }, [currentSettings]);

  const loadPresets = async () => {
    try {
      const stored = await AsyncStorage.getItem(PRESETS_KEY);
      if (stored) {
        const customPresets = JSON.parse(stored);
        setPresets([...DEFAULT_PRESETS, ...customPresets]);
      }
    } catch (error) {
      console.error('Error loading presets:', error);
    }
  };

  const savePresets = async (customPresets) => {
    try {
      await AsyncStorage.setItem(PRESETS_KEY, JSON.stringify(customPresets));
    } catch (error) {
      console.error('Error saving presets:', error);
    }
  };

  const calculateTotalSeconds = (hours, minutes, seconds) => {
    return (
      (parseInt(hours) || 0) * 3600 +
      (parseInt(minutes) || 0) * 60 +
      (parseInt(seconds) || 0)
    );
  };

  const handleApply = () => {
    const player1Time = calculateTotalSeconds(p1Hours, p1Minutes, p1Seconds);
    const player2Time = calculateTotalSeconds(p2Hours, p2Minutes, p2Seconds);
    const increment = parseInt(incrementSeconds) || 0;

    if (player1Time === 0 || player2Time === 0) {
      if (Platform.OS === 'web') {
        window.alert('Please set a time greater than 0 for both players');
      } else {
        Alert.alert('Invalid Time', 'Please set a time greater than 0 for both players');
      }
      return;
    }

    onApply({ player1Time, player2Time, increment });
    onClose();
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      if (Platform.OS === 'web') {
        window.alert('Please enter a name for this preset');
      } else {
        Alert.alert('Name Required', 'Please enter a name for this preset');
      }
      return;
    }

    const player1Time = calculateTotalSeconds(p1Hours, p1Minutes, p1Seconds);
    const player2Time = calculateTotalSeconds(p2Hours, p2Minutes, p2Seconds);
    const increment = parseInt(incrementSeconds) || 0;

    const newPreset = {
      id: Date.now().toString(),
      name: presetName,
      player1Time,
      player2Time,
      increment,
    };

    const customPresets = presets.filter((p) => !DEFAULT_PRESETS.find((d) => d.id === p.id));
    const updatedCustom = [...customPresets, newPreset];

    await savePresets(updatedCustom);
    setPresets([...DEFAULT_PRESETS, ...updatedCustom]);
    setPresetName('');

    if (Platform.OS === 'web') {
      window.alert('Preset saved successfully!');
    } else {
      Alert.alert('Success', 'Preset saved successfully!');
    }
  };

  const handleLoadPreset = (preset) => {
    const p1Total = preset.player1Time;
    setP1Hours(Math.floor(p1Total / 3600).toString());
    setP1Minutes(Math.floor((p1Total % 3600) / 60).toString());
    setP1Seconds((p1Total % 60).toString());

    const p2Total = preset.player2Time;
    setP2Hours(Math.floor(p2Total / 3600).toString());
    setP2Minutes(Math.floor((p2Total % 3600) / 60).toString());
    setP2Seconds((p2Total % 60).toString());

    setIncrementSeconds(preset.increment.toString());
  };

  const handleDeletePreset = async (presetId) => {
    // Only allow deleting custom presets
    if (DEFAULT_PRESETS.find((p) => p.id === presetId)) return;

    const customPresets = presets.filter(
      (p) => !DEFAULT_PRESETS.find((d) => d.id === p.id) && p.id !== presetId
    );

    await savePresets(customPresets);
    setPresets([...DEFAULT_PRESETS, ...customPresets]);
  };

  const formatPresetTime = (preset) => {
    const mins = Math.floor(preset.player1Time / 60);
    return `${mins}+${preset.increment}`;
  };

  const handleClose = () => {
    console.log('Settings close button pressed');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={handleClose}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Clock Settings</Text>
          <TouchableOpacity 
            onPress={handleClose} 
            style={styles.closeButton}
            activeOpacity={0.7}
          >
            <Icon name="close" size={28} color="#333" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Player 1 Time */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Player 1 Time</Text>
            <View style={styles.timeInputRow}>
              <View style={styles.timeInput}>
                <TextInput
                  style={styles.input}
                  value={p1Hours}
                  onChangeText={setP1Hours}
                  keyboardType="number-pad"
                  placeholder="0"
                  maxLength={2}
                />
                <Text style={styles.inputLabel}>Hours</Text>
              </View>
              <Text style={styles.timeSeparator}>:</Text>
              <View style={styles.timeInput}>
                <TextInput
                  style={styles.input}
                  value={p1Minutes}
                  onChangeText={setP1Minutes}
                  keyboardType="number-pad"
                  placeholder="0"
                  maxLength={2}
                />
                <Text style={styles.inputLabel}>Minutes</Text>
              </View>
              <Text style={styles.timeSeparator}>:</Text>
              <View style={styles.timeInput}>
                <TextInput
                  style={styles.input}
                  value={p1Seconds}
                  onChangeText={setP1Seconds}
                  keyboardType="number-pad"
                  placeholder="0"
                  maxLength={2}
                />
                <Text style={styles.inputLabel}>Seconds</Text>
              </View>
            </View>
          </View>

          {/* Player 2 Time */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Player 2 Time</Text>
            <View style={styles.timeInputRow}>
              <View style={styles.timeInput}>
                <TextInput
                  style={styles.input}
                  value={p2Hours}
                  onChangeText={setP2Hours}
                  keyboardType="number-pad"
                  placeholder="0"
                  maxLength={2}
                />
                <Text style={styles.inputLabel}>Hours</Text>
              </View>
              <Text style={styles.timeSeparator}>:</Text>
              <View style={styles.timeInput}>
                <TextInput
                  style={styles.input}
                  value={p2Minutes}
                  onChangeText={setP2Minutes}
                  keyboardType="number-pad"
                  placeholder="0"
                  maxLength={2}
                />
                <Text style={styles.inputLabel}>Minutes</Text>
              </View>
              <Text style={styles.timeSeparator}>:</Text>
              <View style={styles.timeInput}>
                <TextInput
                  style={styles.input}
                  value={p2Seconds}
                  onChangeText={setP2Seconds}
                  keyboardType="number-pad"
                  placeholder="0"
                  maxLength={2}
                />
                <Text style={styles.inputLabel}>Seconds</Text>
              </View>
            </View>
          </View>

          {/* Increment */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Increment (seconds per move)</Text>
            <TextInput
              style={styles.incrementInput}
              value={incrementSeconds}
              onChangeText={setIncrementSeconds}
              keyboardType="number-pad"
              placeholder="0"
              maxLength={3}
            />
          </View>

          {/* Save Preset */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Save as Preset</Text>
            <View style={styles.savePresetRow}>
              <TextInput
                style={styles.presetNameInput}
                value={presetName}
                onChangeText={setPresetName}
                placeholder="Preset name (e.g., My Custom 5+3)"
                maxLength={30}
              />
              <TouchableOpacity
                style={styles.savePresetButton}
                onPress={handleSavePreset}
              >
                <Icon name="save" size={24} color="white" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Presets List */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Presets</Text>
            {presets.map((preset) => (
              <View key={preset.id} style={styles.presetItem}>
                <TouchableOpacity
                  style={styles.presetButton}
                  onPress={() => handleLoadPreset(preset)}
                >
                  <Text style={styles.presetName}>{preset.name}</Text>
                  <Text style={styles.presetTime}>{formatPresetTime(preset)}</Text>
                </TouchableOpacity>
                {!DEFAULT_PRESETS.find((p) => p.id === preset.id) && (
                  <TouchableOpacity
                    style={styles.deleteButton}
                    onPress={() => handleDeletePreset(preset.id)}
                  >
                    <Icon name="delete" size={20} color="#FF4444" />
                  </TouchableOpacity>
                )}
              </View>
            ))}
          </View>
        </ScrollView>

        {/* Apply Button */}
        <View style={styles.footer}>
          <TouchableOpacity style={styles.applyButton} onPress={handleApply}>
            <Text style={styles.applyButtonText}>Apply Settings</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingTop: Platform.OS === 'ios' ? 50 : 16,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    padding: 8,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  timeInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  timeInput: {
    flex: 1,
    alignItems: 'center',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
    width: '100%',
  },
  inputLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  timeSeparator: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginHorizontal: 4,
  },
  incrementInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 18,
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
  },
  savePresetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  presetNameInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
  },
  savePresetButton: {
    backgroundColor: '#94EB98',
    width: 48,
    height: 48,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  presetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  presetButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    marginRight: 8,
  },
  presetName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  presetTime: {
    fontSize: 14,
    color: '#666',
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  deleteButton: {
    padding: 12,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  applyButton: {
    backgroundColor: '#94EB98',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  applyButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
  },
});

export default ChessClockSettings;

